import React, { useEffect, useState } from 'react'
import { Accordion, AccordionDetails, AccordionSummary, Box, CircularProgress, Grid, Typography } from "@mui/material";
import InfiniteScroll from "react-infinite-scroll-component";
import { useDispatch } from "react-redux";
import { AgGridReact } from "ag-grid-react";
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
// icons
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
// utils and helpers
import { openSnackbar } from "../../redux/slices/common";
// custom components
import NoDataFound from "common/component/NoDataFound";
import StatusText from "common/component/StatusText";
import { defaultColDef } from "./CommonAgGridTable";

const defaultPage = {
  page: 1,
  limit: 20
}

const AgGridAccordionTable = ({
  tableName,
  columnDefs,
  expandedColumnDefs,
  refreshTable,
  getDataMethod = () => { },
  customFilters = {},
  renderHeader = true,
  onChildCellClicked,
}) => {
  const dispatch = useDispatch()

  const [dataList, setDataList] = useState({});
  const [expanded, setExpanded] = useState("");
  const [paginationDetails, setPaginationDetails] = useState(defaultPage);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(true);

  const getHeight = (childrenLength = 0) => Math.min((childrenLength * 56) + 39, 350)

  const handleExpandRegion = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : "");
  };

  const handleDataList = async (filters = {}) => {
    try {
      setIsLoading(true);
      const request = { ...customFilters, ...paginationDetails, ...filters }
      const response = await dispatch(getDataMethod(request)).unwrap();
      if (response?.data) {
        let myData = {};

        if (request.page === 1) {
          myData = response
        } else {
          myData = { ...response, data: [...dataList.data, ...response.data] }
        }
        setDataList(myData);

        const hasMoreData = response?.total > myData?.data?.length;
        if (hasMoreData) {
          setPaginationDetails(prev => ({ ...prev, page: prev.page + 1 }))
        } else {
          setHasMore(false)
        }
      }
    } catch (error) {
      const errorMsg = error?.response?.data?.message || error?.message || error;
      dispatch(openSnackbar({ content: errorMsg, color: "error", icon: "warning", }));
    } finally {
      setIsLoading(false)
    }
  }

  const getTableSpecificData = (data) => {
    let childrenLength = 0;

    switch (tableName) {
      case "Regions Listing":
        childrenLength = data?.subRegions?.length || 0;
        return {
          childrenLength,
          rowId: data?.regionId,
          children: data?.subRegions,
          childrenTableHeight: getHeight(childrenLength)
        };
      case "Distribution Management":
        childrenLength = data?.regions?.length || 0;
        return {
          childrenLength,
          rowId: data?.distributionId,
          children: data?.regions,
          childrenTableHeight: getHeight(childrenLength)
        };
      case "Refill logs":
        childrenLength = data?.regions?.length || 0;
        return {
          childrenLength,
          rowId: data?.refillId,
          children: data?.regions,
          childrenTableHeight: getHeight(childrenLength)
        };
      case "Kit verification":
        childrenLength = data?.regions?.length || 0;
        return {
          childrenLength,
          rowId: data?.distributionType,
          children: data?.regions?.map(region => ({ ...region, date: region?.date || data?.date || "" })),
          childrenTableHeight: getHeight(childrenLength)
        };
      case "Assign Template Kit Products":
        childrenLength = data?.products?.length || 0;
        return {
          childrenLength,
          rowId: data?._id,
          children: data?.products,
          childrenTableHeight: getHeight(childrenLength)
        };
      default:
        return {
          childrenLength,
          rowId: "",
          children: [],
          childrenTableHeight: getHeight(childrenLength)
        };
    }
  }

  useEffect(() => {
    handleDataList(defaultPage)
  }, [refreshTable, customFilters]);

  const GridRow = ({ data }) => {
    return (
      <Box sx={{ width: "100%" }} className="accordion-header">
        {
          columnDefs?.map((column, index) => {
            const cellData = column.cellRenderer ? column.cellRenderer({ data }) : column.valueGetter ? column.valueGetter({ data }) : data[column.field] || "-";

            return (
              column.field === "status"
                ? <Typography key={index} className={`${column?.className || ''}`}>
                  <StatusText status={typeof data?.status === "boolean" ? data?.status ? "Active" : "Inactive" : data.status} />
                </Typography>
                : <Typography key={index} className={`${column?.className || ''}`}>
                  {cellData}
                </Typography>
            )
          })
        }
      </Box>
    )
  }

  return (
    <>
      <Grid container alignItems='center' className="table-with-accordion">
        <Grid item xs={12} sm={12}>
          {/* Table header */}
          {
            renderHeader &&
            <div className="cus-table-header">
              {
                columnDefs?.map((item, index) => {
                  return (
                    <Typography key={index} className={`${item?.className || ''}`} style={{ minWidth: 150 }}>
                      {item.headerName}
                    </Typography>
                  )
                })
              }
            </div>
          }
          {isLoading && !dataList?.data?.length ?
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", paddingBlock: 20, textAlign: "center", minHeight: "360px" }}>
              <CircularProgress size={32} />
            </div>
            : <div
              id={`scrollableDiv-${tableName}`}
              style={{ height: "55vh", width: "100%", overflowY: "auto" }}
            >
              <InfiniteScroll
                dataLength={dataList?.data?.length || 0}
                next={handleDataList}
                hasMore={hasMore}
                loader={
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", paddingBlock: 20, textAlign: "center", minHeight: "360px" }}>
                    <CircularProgress size={32} />
                  </div>
                }
                scrollableTarget={`scrollableDiv-${tableName}`}
              >
                {
                  dataList?.data?.length > 0 ?
                    dataList?.data?.map((data, index) => {
                      const tableSpecificData = getTableSpecificData(data);

                      return (
                        !tableSpecificData?.childrenLength
                          ? <Accordion key={index} className="accordion-container">
                            <AccordionSummary expandIcon={<span style={{ minWidth: "14px", minHeight: "1px" }} />}>
                              <GridRow data={data} />
                            </AccordionSummary>
                          </Accordion>
                          : <Accordion
                            key={index}
                            className="accordion-container"
                            expanded={expanded === tableSpecificData?.rowId}
                            onChange={handleExpandRegion(tableSpecificData?.rowId)}>
                            <AccordionSummary
                              expandIcon={<ExpandMoreIcon />}
                              aria-controls={`${tableSpecificData?.rowId}-heading-content`}
                              id={`panelheading-${tableSpecificData?.rowId}`}
                            >
                              <GridRow data={data} />
                            </AccordionSummary>
                            <AccordionDetails>
                              <Box>
                                {
                                  <div className="ag-theme-quartz" style={{ height: `${tableSpecificData?.childrenTableHeight || 39}px`, width: "100%" }}>
                                    <AgGridReact
                                      className="ag-grid--table"
                                      pagination={false}
                                      rowData={tableSpecificData?.children}
                                      noRowsOverlayComponent={NoDataFound}
                                      overlayLoadingTemplate={'<span class="table-loader"></span>'}
                                      columnDefs={expandedColumnDefs}
                                      rowModelType={"clientSide"}
                                      headerHeight={36}
                                      rowHeight={56}
                                      rowSelection="multiple"
                                      suppressRowClickSelection={true}
                                      context={{ parentData: data }}
                                      defaultColDef={{ ...defaultColDef }}
                                      onCellClicked={onChildCellClicked}
                                    />
                                  </div>
                                }
                              </Box>
                            </AccordionDetails>
                          </Accordion>
                      )
                    })
                    : dataList?.data?.length === 0 ?
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", paddingBlock: 20, textAlign: "center", minHeight: "360px" }}><NoDataFound /></div>
                      : null
                }
              </InfiniteScroll>
            </div>}
        </Grid>
      </Grid>
    </>
  )
}

export default AgGridAccordionTable